import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import { EmptyGuid } from '../../../shared/constants';
import { useApi } from '../../../shared/context';
import { QueryKeys, useFinancialCovenantTypesQuery } from '../../../shared/hooks';
import {
	TermSheetFinancialCovenant,
	CovenantTargetTypes,
	CovenantTargetTypesMap,
	CovenantValueTypes,
	CovenantValueTypesMap,
} from '../../../shared/models';
import { FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface CovenantFormProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	covenant: TermSheetFinancialCovenant | null;
	close: () => void;
}

export const CovenantForm: React.FC<CovenantFormProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	covenant,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const typesQuery = useFinancialCovenantTypesQuery();

	const covenantMutation = useMutation(
		async ({ values }: CovenantFormMutationVariables) => {
			const patchValues = {
				financialCovenantTypeGuid: values.financialCovenantTypeGuid,
				covenantName: values.covenantName,
				covenantTargetType: values.covenantTargetType,
				covenantTarget: NumberUtils.ParseDecimal(values.covenantTarget, undefined),
				isTargetDefined: values.isTargetDefined,
				CovenantValueType: values.covenantValueType,
			};

			if (covenant != null && covenant.id == 0) {
				const { data, error } = await getApiResponseData<void>(
					api.post(
						CerebroApiEndpoints.TermSheets.Covenant(dataRoomGuid, termSheetGuid, revisionGuid),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			} else if (covenant != null && covenant.id > 0) {
				const { data, error } = await getApiResponseData<void>(
					api.patch(
						CerebroApiEndpoints.TermSheets.CovenantSpecific(
							dataRoomGuid,
							termSheetGuid,
							revisionGuid,
							covenant?.guid
						),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Term sheet revision was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={covenant != null} onClose={close}>
			<Formik
				initialValues={{
					financialCovenantTypeGuid: covenant?.financialCovenantTypeGuid,
					covenantName: covenant?.covenantName ?? '',
					covenantTargetType: covenant?.covenantTargetType ?? 'GreaterThanOrEqual',
					covenantTarget: NumberUtils.FormatFloat(covenant?.covenantTarget, 2),
					isTargetDefined: covenant?.isTargetDefined ?? true,
					covenantValueType: 'Numeric',
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(covenantMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="financialCovenantTypeGuid" required>
										Covenant Type
									</CerebroLabel>
									{typesQuery.isLoading && <Skeleton height={60} />}
									{typesQuery.isSuccess && (
										<Field
											id="financialCovenantTypeGuid"
											as={CerebroDropdown}
											name="financialCovenantTypeGuid"
											disabled={isSubmitting}
											error={
												touched.financialCovenantTypeGuid &&
												errors.financialCovenantTypeGuid !== undefined
											}
										>
											{typesQuery.data.map(type => (
												<CerebroDropdownMenuItem key={type.guid} value={type.guid}>
													{type.covenantName}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									)}
									{errors.financialCovenantTypeGuid && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.financialCovenantTypeGuid}
										</CerebroText>
									)}
								</Grid>

								{values.financialCovenantTypeGuid == EmptyGuid && (
									<>
										<Grid item xs={11}>
											<CerebroLabel id="covenantName">Covenant Name</CerebroLabel>
											<Field
												id="covenantName"
												as={CerebroInput}
												name="covenantName"
												disabled={isSubmitting}
												error={touched.covenantName && errors.covenantName !== undefined}
											/>
											{errors.covenantName && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.covenantName}
												</CerebroText>
											)}
										</Grid>

										<Grid item xs={11}>
											<CerebroLabel id="covenantValueType">Covenant Value Type</CerebroLabel>

											<Field
												id="covenantValueType"
												as={CerebroDropdown}
												name="covenantValueType"
												disabled={isSubmitting}
												error={
													touched.covenantValueType && errors.covenantValueType !== undefined
												}
											>
												{Object.entries(CovenantValueTypesMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>

											{errors.covenantValueType && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.covenantValueType}
												</CerebroText>
											)}
										</Grid>
									</>
								)}

								<Grid item xs={11}>
									<CerebroLabel id="isTargetDefined">
										Is a target value defined for this covenant?
									</CerebroLabel>
									<Field
										id="isTargetDefined"
										as={CerebroYesNo}
										name="isTargetDefined"
										disabled={isSubmitting}
									/>
								</Grid>

								{values.isTargetDefined && (
									<>
										<Grid item xs={11}>
											<CerebroLabel id="covenantTargetType" required>
												Target
											</CerebroLabel>
										</Grid>
										<Grid item xs={4}>
											<Field
												id="covenantTargetType"
												as={CerebroDropdown}
												name="covenantTargetType"
												disabled={isSubmitting}
											>
												{Object.entries(CovenantTargetTypesMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>
										</Grid>
										<Grid item xs={7}>
											<Field
												id="covenantTarget"
												as={CerebroMaskedInput}
												allowDecimals={true}
												minDecimalPlaces={1}
												maxDecimalPlaces={2}
												name="covenantTarget"
												disabled={isSubmitting}
												error={touched.covenantTarget && errors.covenantTarget !== undefined}
											/>
											{errors.covenantTarget && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.covenantTarget}
												</CerebroText>
											)}
										</Grid>
									</>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type CovenantFormMutationVariables = FormikUtils.MutateOnSubmitVariables<CovenantFormValues>;
interface CovenantFormValues {
	financialCovenantTypeGuid: string | undefined;
	covenantName: string;
	covenantTargetType: CovenantTargetTypes;
	covenantTarget: string;
	isTargetDefined: boolean;
	covenantValueType: CovenantValueTypes;
}
type CovenantFormValueErrors = FormikErrors<CovenantFormValues>;

function validateForm(values: CovenantFormValues) {
	const errors: CovenantFormValueErrors = {};

	if (!values.financialCovenantTypeGuid || values.financialCovenantTypeGuid === '') {
		errors.financialCovenantTypeGuid = 'You must select a covenant type.';
	}

	if (values.financialCovenantTypeGuid == EmptyGuid) {
		if (!values.covenantName || values.covenantName === '') {
			errors.covenantName = 'You must enter a covenant name.';
		}
	}

	if (values.isTargetDefined) {
		if (!values.covenantTarget || values.covenantTarget === '') {
			errors.covenantTarget = 'You must enter a valid covenant target.';
		}
	}

	return errors;
}
